'use client';

import { useTranslations } from 'next-intl';
import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSetAvatar } from '@/lib/api/rewards-hub/hooks';
import type {
  AvatarFrameId,
  RewardsProfile,
  Scale,
  ScaleAvatar,
} from '@/lib/api/rewards-hub/models';
import { Dialog, DialogContent, DialogTitle } from '@/lib/ui-kit/dialog';
import { Icon } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';
import { Switch } from '@/lib/ui-kit-v2/switch';
import { AvatarPortrait } from './AvatarPortrait';
import { AVATAR_FRAMES, type AvatarFrame, avatarArt, isFrameUnlocked } from './avatarTheme';
import { focusDialogOnOpen } from './dialogFocus';
import { TITLE_GRADIENT } from './rewardsTheme';

/**
 * "Choose Your Avatar" — the portrait and the frames worn over it, on two tabs
 * (Figma 13449:469105; the frame tab is 13878:183036, where the frames stack —
 * any of the opened ones at once, product 30.10).
 *
 * The catalogue is the whole ladder, locked ranks included: the design shows
 * what is still to come rather than hiding it, and the FE notes say the same of
 * the collection — an unearned thing is a reason to play, not a spoiler.
 *
 * Nothing is written until Save, so the dialog holds its own draft.
 */

type PickerTab = 'avatar' | 'frame';

/** The chosen tab is a raised pill on the sunken track (Figma's Toggle). */
const TAB_ACTIVE = 'bg-v2-surface-tertiary text-v2-text-primary hover:text-v2-text-primary';
const TAB_IDLE = 'text-v2-text-secondary';

/*
 * Below md the dialog follows the 768 and 360 frames (13878:205574,
 * 13878:206178); from md up it is the 700 px desktop dialog of 13878:183036.
 */

/** The toggle is 40 px high at every width: 32 px pills on a 4 px track. */
const TAB_COMPACT = 'h-8 rounded-[8px] font-medium';

/**
 * Below md a portrait fills its grid column: 72.5 px eight across at 768,
 * 65 px four across at 360, and smaller still on a narrower phone.
 */
const PORTRAIT_FLUID = 'max-md:block max-md:aspect-square max-md:h-auto max-md:w-full';

/**
 * The frames' title wears the metallic fill and sits between a blank 40 px
 * slot and the close button, so it stays centred.
 */
const TITLE_STYLE = { '--title-gradient': TITLE_GRADIENT } as CSSProperties;

/**
 * The frame tab's hero sits in a blue bloom (Figma: a radial fill at 35%
 * over the dialog's surface, 506 × 241 px in the 764 × 260 hero — kept as
 * ratios so the bloom keeps its shape on a phone).
 */
const FRAME_HERO_STYLE: CSSProperties = {
  backgroundImage: `radial-gradient(33.07% 46.3% at 50% 50%,
    rgba(24,113,255,0.35) 0%, rgba(18,85,191,0.2625) 25%, rgba(12,56,127,0.175) 50%,
    rgba(6,28,64,0.0875) 75%, rgba(0,0,0,0) 100%)`,
};

/** Figma's switch is a pill: round track, round thumb, blue/500 when on. */
const FRAME_SWITCH_CLASS = 'rounded-full data-[state=checked]:bg-blue-500 [&>span]:rounded-full';

interface AvatarPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: RewardsProfile;
  scale: Scale | undefined;
  /** Shown under the hero on the frame tab. */
  username?: string;
  /**
   * The rank whose reward was just claimed: its row plays the unlock (05.10,
   * Figma 13913:39068) the first time the picker opens after the claim, and
   * its avatars count as the player's before `/profile` is re-read.
   */
  unlockRankIndex?: number | null;
  /** The unlock has played; the next open shows the row as it simply is. */
  onUnlockShown?: () => void;
}

/**
 * The row unlock, off the 05.10 motion table: 1.2 s locked, five ~70 ms
 * swings of the whole row, 300 ms for the padlocks to go, 600 ms for the art
 * and the label to come up. The beats live in CSS; this is when it is over.
 */
const ROW_UNLOCK_MS = 1200 + 350 + 300 + 600;

/** The catalogue grouped by rank, in ladder order. */
function avatarsByRank(
  scale: Scale | undefined,
): { rankIndex: number; title: string; items: ScaleAvatar[] }[] {
  const avatars = scale?.avatars ?? [];
  return (scale?.ranks ?? [])
    .map((rank) => ({
      rankIndex: rank.index,
      title: rank.title,
      items: avatars
        .filter((avatar) => avatar.rankIndex === rank.index)
        .sort((a, b) => a.position - b.position),
    }))
    .filter((section) => section.items.length > 0);
}

interface AvatarCellProps {
  avatar: ScaleAvatar;
  locked: boolean;
  selected: boolean;
  onSelect: (id: string) => void;
  /** Playing the row unlock; `index` sets which way this one swings. */
  unlocking?: boolean;
  index?: number;
}

function AvatarCell({
  avatar,
  locked,
  selected,
  onSelect,
  unlocking = false,
  index = 0,
}: AvatarCellProps) {
  const t = useTranslations('rewardsHub.avatars');
  const handleClick = useCallback(() => onSelect(avatar.id), [avatar.id, onSelect]);

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={locked}
      aria-pressed={selected}
      aria-label={locked ? t('lockedAvatar') : t('chooseAvatar')}
      className={cn(
        'relative rounded-full transition-transform focus-visible:outline-2 focus-visible:outline-v2-btn-primary',
        locked ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-105',
        unlocking && 'rewards-avatar-shake',
      )}
      // Neighbours swing opposite ways.
      style={unlocking ? ({ '--shake-dir': index % 2 ? -1 : 1 } as CSSProperties) : undefined}
    >
      <AvatarPortrait
        src={avatarArt(avatar)}
        size={84}
        locked={locked}
        selected={selected}
        unlocking={unlocking}
        className={PORTRAIT_FLUID}
      />
      {locked || unlocking ? (
        <Icon
          name="Locked"
          size={20}
          className={cn(
            '-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 text-white',
            unlocking && 'rewards-lock-gone',
          )}
        />
      ) : null}
    </button>
  );
}

interface FrameCardProps {
  /** `null` is the "None" card: no frame at all. */
  frame: AvatarFrame | null;
  portrait: string;
  rankIndex: number | null;
  rankTitle: string;
  /** None: no frame is worn. A frame: it is one of the worn ones. */
  active: boolean;
  onToggle: (id: AvatarFrameId | null, on: boolean) => void;
}

/**
 * One card of the strip (Figma card-none / -master / -oracle / -legend): the
 * player's portrait wearing this frame alone, the switch, and what it takes.
 * A locked one is dimmed under a padlock, its switch out of reach.
 */
function FrameCard({ frame, portrait, rankIndex, rankTitle, active, onToggle }: FrameCardProps) {
  const t = useTranslations('rewardsHub.avatars');
  const unlocked = frame === null || isFrameUnlocked(frame, rankIndex);
  const frames = useMemo(() => (frame ? [frame.id] : []), [frame]);
  const label = t(`frames.${frame?.id ?? 'NONE'}`);
  const handleChange = useCallback(
    (on: boolean) => onToggle(frame?.id ?? null, on),
    [frame, onToggle],
  );

  return (
    // Figma's stroke takes no room, so 15 px of padding keeps the card 124 px tall.
    <div className="flex min-w-0 flex-col items-center justify-center gap-2 rounded-[20px] border border-v2-border px-[11px] py-[15px]">
      <div className="flex w-full items-start justify-between">
        {/* Figma balances the switch with an invisible twin, so the swatch is centred. */}
        <span aria-hidden className="h-5 w-9 shrink-0" />
        <span className="relative flex size-[52px] shrink-0 items-center justify-center">
          <span className={cn('flex', !unlocked && 'opacity-40')}>
            <AvatarPortrait src={portrait} size={42} frames={frames} />
          </span>
          {unlocked ? null : (
            <Icon
              name="Locked"
              size={16}
              className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 text-white"
            />
          )}
        </span>
        <Switch
          checked={active}
          disabled={!unlocked}
          onCheckedChange={handleChange}
          aria-label={label}
          className={FRAME_SWITCH_CLASS}
        />
      </div>
      <div className="flex w-full flex-col items-center gap-1 text-center">
        <span className="text-[12px] leading-4 tracking-[-0.12px] text-v2-text-primary">
          {label}
        </span>
        <span className="text-[10px] leading-[1.2] text-v2-text-secondary">
          {frame === null
            ? t('frameDefault')
            : unlocked
              ? t('frameUnlocked', { rank: rankTitle })
              : t('frameLocked', { rank: rankTitle })}
        </span>
      </div>
    </div>
  );
}

export function AvatarPickerDialog({
  open,
  onOpenChange,
  profile,
  scale,
  username,
  unlockRankIndex = null,
  onUnlockShown,
}: AvatarPickerDialogProps) {
  const t = useTranslations('rewardsHub.avatars');
  const tCommon = useTranslations('common');
  const [tab, setTab] = useState<PickerTab>('avatar');
  const [avatarId, setAvatarId] = useState<string | null>(profile.avatar?.id ?? null);
  const [frames, setFrames] = useState<AvatarFrameId[]>(profile.avatar?.frames ?? []);
  const { mutate: save, isPending } = useSetAvatar();

  const sections = useMemo(() => avatarsByRank(scale), [scale]);
  // The claimed rank's avatars are the player's from the claim on, whether or
  // not `/profile` has been read again since.
  const owned = useMemo(() => {
    const ids = new Set(profile.availableAvatarIds);
    for (const avatar of scale?.avatars ?? []) {
      if (avatar.rankIndex === unlockRankIndex) ids.add(avatar.id);
    }
    return ids;
  }, [profile.availableAvatarIds, scale, unlockRankIndex]);

  // The unlock plays once per claim, on the open that follows it, with the
  // row brought into view. Switching tabs does not replay it.
  const [unlocking, setUnlocking] = useState<number | null>(null);
  const unlockRowRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (open && unlockRankIndex !== null) setUnlocking(unlockRankIndex);
  }, [open, unlockRankIndex]);
  useEffect(() => {
    if (unlocking === null) return;
    unlockRowRef.current?.scrollIntoView({ block: 'nearest' });
    const timer = setTimeout(() => {
      setUnlocking(null);
      onUnlockShown?.();
    }, ROW_UNLOCK_MS);
    return () => clearTimeout(timer);
  }, [unlocking, onUnlockShown]);

  // Reopening starts from what the player is actually wearing, not the draft
  // they walked away from.
  useEffect(() => {
    if (!open) return;
    setTab('avatar');
    setAvatarId(profile.avatar?.id ?? null);
    setFrames(profile.avatar?.frames ?? []);
  }, [open, profile.avatar]);

  const chosen = useMemo(
    () => (scale?.avatars ?? []).find((avatar) => avatar.id === avatarId) ?? null,
    [scale, avatarId],
  );
  const portrait = chosen ? avatarArt(chosen) : '';
  const rankTitleOf = useCallback(
    (index: number) => scale?.ranks.find((rank) => rank.index === index)?.title ?? '',
    [scale],
  );

  const handleAvatarTab = useCallback(() => setTab('avatar'), []);
  const handleFrameTab = useCallback(() => setTab('frame'), []);
  const handleCancel = useCallback(() => onOpenChange(false), [onOpenChange]);
  const handleSave = useCallback(() => {
    if (avatarId) save({ avatarId, frames });
    onOpenChange(false);
  }, [avatarId, frames, onOpenChange, save]);
  // The frames stack: each switch adds or takes off its own, and "None" takes
  // them all off. None cannot be switched off by itself — put a frame on.
  const handleFrameToggle = useCallback((id: AvatarFrameId | null, on: boolean) => {
    if (id === null) {
      if (on) setFrames([]);
      return;
    }
    // A code sent twice is a 400, so putting one on never adds a second copy.
    setFrames((worn) => {
      const others = worn.filter((one) => one !== id);
      return on ? [...others, id] : others;
    });
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onOpenAutoFocus={focusDialogOnOpen}
        // The kit's close button gives way to the frames' plain cross in the
        // title row. From md up the dialog is Figma's 700 px box on either tab.
        className="flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] flex-col gap-4
          overflow-hidden rounded-[20px] border-v2-border bg-v2-surface-primary px-4 py-5 outline-none
          sm:w-[calc(100vw-3.5rem)] sm:max-w-[804px] sm:gap-6 sm:px-6 sm:py-8 md:h-[700px]
          [&>[data-slot=dialog-close]]:hidden"
      >
        <div className="flex shrink-0 items-center">
          <span aria-hidden className="size-10 shrink-0" />
          <DialogTitle
            // -mx-2 lets the title run into the empty sides of the two 40 px
            // slots, as in the 360 frame, where the 230 px title overlaps them.
            className="min-w-0 flex-1 bg-(image:--title-gradient) bg-clip-text pr-0 text-center text-[24px]
              font-semibold leading-8 text-transparent max-md:-mx-2"
            style={TITLE_STYLE}
          >
            {t('title')}
          </DialogTitle>
          <button
            type="button"
            onClick={handleCancel}
            aria-label={tCommon('close')}
            className="flex size-10 shrink-0 cursor-pointer items-center justify-center text-white opacity-80 transition-opacity hover:opacity-100 focus-visible:outline-2 focus-visible:outline-v2-btn-primary"
          >
            <Icon name="Close" size={24} />
          </button>
        </div>

        <div className="flex shrink-0 gap-1 rounded-[10px] bg-v2-surface-secondary p-1">
          <Button
            appearance="ghost"
            size="M"
            className={cn('flex-1', TAB_COMPACT, tab === 'avatar' ? TAB_ACTIVE : TAB_IDLE)}
            onClick={handleAvatarTab}
          >
            {t('tabAvatar')}
          </Button>
          <Button
            appearance="ghost"
            size="M"
            className={cn('flex-1', TAB_COMPACT, tab === 'frame' ? TAB_ACTIVE : TAB_IDLE)}
            onClick={handleFrameTab}
          >
            {t('tabFrame')}
          </Button>
        </div>

        {/* The list and the button sit 16 px apart in the 768/360 frames. */}
        <div className="flex min-h-0 flex-1 flex-col gap-4 md:gap-6">
          {tab === 'avatar' ? (
            <div
              // Below md the list is the frames' fixed window (380 px at 360,
              // 450 px at 768) and gives way on a short screen. Its scrollbar
              // lives in the dialog's padding: with a phone's overlay bar the
              // grid gets the frames' full width (296 / 664 px).
              className="scrollbar-rail flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto pr-1
                max-md:-mr-2 max-md:flex-[0_1_380px] max-md:gap-4 max-md:pr-2 sm:max-md:flex-[0_1_450px]"
            >
              {sections.map((section) => {
                const unlockedRank =
                  profile.rankIndex !== null && profile.rankIndex >= section.rankIndex;
                const isUnlocking = section.rankIndex === unlocking;
                return (
                  <section
                    key={section.rankIndex}
                    ref={isUnlocking ? unlockRowRef : undefined}
                    className="flex flex-col gap-3"
                  >
                    <header className="flex items-baseline justify-between gap-3 max-md:items-center">
                      <h3 className="text-[15px] font-semibold text-v2-text-primary max-md:leading-[19px]">
                        {section.title}
                      </h3>
                      {isUnlocking ? (
                        // "Locked" gives way to "Unlocked" as the row lights up.
                        <span className="grid text-[13px] text-v2-text-secondary max-md:text-[12px] max-md:leading-[1.5]">
                          <span className="rewards-label-out col-start-1 row-start-1 text-right">
                            {t('lockedRank')}
                          </span>
                          <span className="rewards-label-in col-start-1 row-start-1 text-right max-md:text-v2-text-primary">
                            {t('unlocked')}
                          </span>
                        </span>
                      ) : (
                        <span
                          className={cn(
                            'text-[13px] text-v2-text-secondary max-md:text-[12px] max-md:leading-[1.5]',
                            // The frames light an open rank's label up.
                            unlockedRank && 'max-md:text-v2-text-primary',
                          )}
                        >
                          {unlockedRank ? t('unlocked') : t('lockedRank')}
                        </span>
                      )}
                    </header>
                    <div className="grid grid-cols-4 gap-3 sm:grid-cols-8">
                      {section.items.map((avatar, i) => (
                        <AvatarCell
                          key={avatar.id}
                          avatar={avatar}
                          locked={!isUnlocking && !owned.has(avatar.id)}
                          selected={avatar.id === avatarId}
                          onSelect={setAvatarId}
                          unlocking={isUnlocking}
                          index={i}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}
              {sections.length === 0 ? (
                <p className="text-[13px] text-v2-text-secondary">{t('empty')}</p>
              ) : null}
            </div>
          ) : (
            // Figma spreads hero, strip and button over the 700 px box.
            <div className="flex min-h-0 flex-1 flex-col justify-between gap-6 overflow-y-auto">
              <div
                className="flex shrink-0 flex-col items-center justify-center gap-4 rounded-[20px] px-6 py-8"
                style={FRAME_HERO_STYLE}
              >
                {/* 148 px leaves the wreath and the sparkles room round the 120 px portrait. */}
                <span className="flex size-[148px] items-center justify-center">
                  <AvatarPortrait src={portrait} size={120} frames={frames} />
                </span>
                {username ? (
                  <span className="max-w-full truncate text-center text-[24px] font-[500] leading-8 text-v2-text-primary">
                    {username}
                  </span>
                ) : null}
              </div>
              <div className="flex shrink-0 flex-col gap-3">
                <h3 className="text-[14px] font-semibold leading-[17px] text-v2-text-primary">
                  {t('chooseFrame')}
                </h3>
                {/* Four across as in Figma; two by two where four do not fit. */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <FrameCard
                    frame={null}
                    portrait={portrait}
                    rankIndex={profile.rankIndex}
                    rankTitle=""
                    active={frames.length === 0}
                    onToggle={handleFrameToggle}
                  />
                  {AVATAR_FRAMES.map((frame) => (
                    <FrameCard
                      key={frame.id}
                      frame={frame}
                      portrait={portrait}
                      rankIndex={profile.rankIndex}
                      rankTitle={rankTitleOf(frame.unlockRank)}
                      active={frames.includes(frame.id)}
                      onToggle={handleFrameToggle}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex shrink-0 items-center gap-2">
            {/* The 768/360 frames and the frame tab have the one button; the cross closes. */}
            {tab === 'avatar' ? (
              <Button appearance="neutral" className="flex-1 max-md:hidden" onClick={handleCancel}>
                {t('cancel')}
              </Button>
            ) : null}
            <Button
              className="flex-1"
              onClick={handleSave}
              disabled={isPending || avatarId === null}
            >
              {tab === 'avatar' ? t('select') : t('save')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
